import * as React from 'react';
import { NavLink, type NavLinkProps } from 'react-router-dom';

import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'classnames';
import { omit } from 'lodash';

import { Spacer } from '@shared/components/helpers';

export type ButtonKind =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark'
  | 'link'
  | 'mint'
  | 'default';
export type ButtonSize = 'sm' | 'lg';

type ButtonProps = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;
type AnchorProps = React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>;

interface IButtonStylingProps {
  active?: boolean;
  block?: boolean;
  outline?: boolean;
  kind?: ButtonKind;
  size?: ButtonSize;
  className?: string;
}

interface IButtonStateProps {
  disabled?: boolean;
  disabledLabel?: string;
  loading?: boolean;
  loadingLabel?: string;
}

const styleAndStateKeys = [
  'active',
  'block',
  'outline',
  'kind',
  'size',
  'className',
  'disabled',
  'disabledLabel',
  'loading',
  'loadingLabel',
];

function generateClassName({
  active,
  block,
  outline,
  kind,
  size,
  className,
  disabled,
  loading,
}: IButtonStylingProps & IButtonStateProps) {
  return cn(
    'btn',
    active && 'active',
    kind && (outline ? `btn-outline-${kind}` : `btn-${kind}`),
    size && `btn-${size}`,
    block && 'btn-block',
    (loading || disabled) && 'disabled',
    className,
  );
}

function generateLabel({
  children,
  disabled,
  disabledLabel,
  loading,
  loadingLabel,
  size,
}: IButtonStateProps & { children?: React.ReactNode; disabled?: boolean; size?: ButtonSize }) {
  if (disabled) {
    return disabledLabel || children;
  } else if (loading) {
    return (
      <>
        <FontAwesomeIcon icon={faSpinner} spin />
        <Spacer width={(size === 'sm' && '4px') || (size === 'lg' && '8px') || '6px'} inline />
        {loadingLabel || children}
      </>
    );
  }

  return children;
}

export const ButtonGroup: React.FC = ({ children }) => (
  <div className="btn-group" role="group">
    {children}
  </div>
);

export const Button: React.FC<ButtonProps & IButtonStylingProps & IButtonStateProps> = ({
  type = 'button',
  ...props
}) => (
  <button
    type={type}
    {...omit(props, styleAndStateKeys)}
    className={generateClassName(props)}
    children={generateLabel(props)}
    disabled={props.loading || props.disabled}
  />
);

export const AnchorButton: React.FC<AnchorProps & IButtonStylingProps & IButtonStateProps> = (props) => (
  <a
    {...omit(props, styleAndStateKeys)}
    className={generateClassName(props)}
    children={generateLabel(props)}
    href={props.disabled || props.loading ? undefined : props.href}
    aria-disabled={props.loading || props.disabled}
  />
);

const isExternalHttpNavDestination = (to: NavLinkProps['to']): to is string =>
  typeof to === 'string' && /^https?:\/\//i.test(to);

export const NavLinkButton: React.FC<Pick<NavLinkProps, 'to' | 'target'> & IButtonStylingProps & IButtonStateProps> = ({
  to,
  target,
  ...props
}) => {
  if (isExternalHttpNavDestination(to)) {
    return (
      <AnchorButton
        {...props}
        href={props.disabled || props.loading ? undefined : to}
        target={target}
        aria-disabled={props.loading || props.disabled}
      />
    );
  }

  return (
    <NavLink
      aria-disabled={props.loading || props.disabled}
      target={target}
      to={to}
      onClick={(event) => {
        if (props.loading || props.disabled) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      className={generateClassName(props)}
      children={generateLabel(props)}
    />
  );
};
